import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Strings from '../../../Strings';
import { actions } from '../../SuperUser/Report/redux/actions';
import { graphContainer } from './graphContainers';
import { ColorPalette } from '../DashboardColorPalettes';
import GenericCategoryGraph, { chartTypeEnum } from '../../../components/Graphs/GenericCategoryGraph';
import { genIntervalName } from './MetricTrendAllIntervals';


const statuses = ["verified", "enrolled", "activated", "ended"];
export const PatientStatusInTimeMetadata = {
  id: 'PatientStatusInTimeWidget',
  name: Strings.widgets.patientStatus,
  permission: '',
  defProps: { i: 'PatientStatusInTimeWidget', w: 4, h: 4, minW: 2, minH: 4 },
  configuration: {
    stackedChartType: {
      type: 'select',
      name: 'stackedChartType',
      label: 'Chart Type',
      defaultValue: chartTypeEnum.stackedPie,
      options: [
        { label: 'Stacked Bar chart', value: chartTypeEnum.stackedBar },
        { label: 'Stacked Horizontal Bar', value: chartTypeEnum.horizontalStackedBar },
        { label: 'Stacked Pie Bar', value: chartTypeEnum.stackedPie },
        { label: 'Grid', value: 'grid' }
      ],
      canBeSetByDashboard: false,
    },
    plotInterval: {
      type: 'select',
      name: 'plotInterval',
      label: 'Period Type',
      defaultValue: 'monthly',
      options: [
        { label: 'Days', value: 'days' },
        { label: 'Weeks', value: 'weeks' },
        { label: 'Months', value: 'months' },
      ],
      canBeSetByDashboard: false,
    },
    rollingPeriod: {
      type: 'select',
      name: 'rollingPeriod',
      label: 'Rolling Period (days / weeks / months)',
      defaultValue: 4,
      options: [
        { label: '2', value: 2 },
        { label: '3', value: 3 },
        { label: '4', value: 4 },
        { label: '5', value: 5 },
        { label: '6', value: 6 },
      ],
      canBeSetByDashboard: false,
    },
    metrics: {
      type: 'multiselect',
      name: 'metrics',
      label: 'Metrics',
      defaultValue: ['enrolled', 'verified', 'activated', 'ended'],
      options: [
        { label: 'Enrolled', value: 'enrolled' },
        { label: 'Verified', value: 'verified' },
        { label: 'Activated', value: 'activated' },
        { label: 'Ended', value: 'ended' },
      ],
      canBeSetByDashboard: false,
    },
  },
};

export const GetPatientStatusInTimeDataSeries = (metrics, colorPalette) => statuses.filter(status => metrics.includes(status)).map((status, i) => (
    {
    name: Strings.capPatient.patientStatus[status],
    color: colorPalette?.interpolate((1 / (statuses.length || 0)) * (i + 1)),
    selector: item => item[status],
  }));

function PatientStatusInTime(props) {
  const [data, setData] = React.useState([]);
  const { colorPalette, refreshTimestamp, graphType, getEnrollmentsMetrics, plotInterval, rollingPeriod, metrics, orgIds } = props;

  React.useEffect(() => {
    getEnrollmentsMetrics({orgId: orgIds && orgIds.length > 0 ? orgIds : undefined}).then(response => {
      const respDataToRead = plotInterval === 'monthly' ? 'months' : plotInterval === 'weekly' ? 'weeks' : 'days';
      if (response?.response?.[respDataToRead]) {
        setData(Object.entries(response?.response?.[respDataToRead])?.map(([key, item]) => ({
          period: genIntervalName(plotInterval, -key),
          verified: item.registered_enrollments,
          enrolled: item.enrolled_enrollments,
          activated: item.activated_enrollments,
          ended: item.disenrolled_enrollments,
        })));
      }
    });
  }, [refreshTimestamp, orgIds, plotInterval, rollingPeriod]);

  const grid = (
    <div className="box2colsWithVl vncols" style={{ gridTemplateColumns: `repeat(${rollingPeriod}, 1fr)` }}>
      {data &&
        data.slice(0, rollingPeriod).map(v => Object.entries(v).filter(([key]) => key !== 'period' && metrics.includes(key)).map(([key, value], index) => (
          <div className="box2colsWithVl-box" key={`${v.plotInterval}-${key}`}>
            <div className="box2colsWithVl-valueBox black">
              {value}
            </div>
            <div className="box2colsWithVl-labelBox">
              {`${Strings.capPatient.patientStatus[key]} ${v.period}`}
            </div>
          </div>
        )))}
    </div>
  );

  const graph = (
    <GenericCategoryGraph
      graphData={data.slice(0, rollingPeriod)}
      hasBorder
      forceCondensed
      xAxisTopPosition={false}
      yAxisWidth={graphType === chartTypeEnum.horizontalStackedBar ? 100 : 40}
      yAxisUnit=""
      tooltipTitle={Strings.status}
      dataSeries={GetPatientStatusInTimeDataSeries(metrics, colorPalette)}
      categorySelector={item => item.period}
      hasReferenceAreas
      graphType={graphType}
      colorPalette={colorPalette}
    />
  );

  return graphContainer(graphType === 'grid' ? grid : graph, 'Number of Patients in States');
}

PatientStatusInTime.propTypes = {
  getPatientsStatistics: PropTypes.func,
  onClick: PropTypes.func,
  colorPalette: PropTypes.any,
  refreshTimestamp: PropTypes.any,
  graphType: PropTypes.string,
  orgIds: PropTypes.arrayOf(PropTypes.string),
  dragging: PropTypes.bool,
};

const mapStateToProps = state => {
  return {
    colorPalette:
      ColorPalette[state.dashboardState.configuration?.allDashboards?.colorPalette] || Object.values(ColorPalette)[0],
      orgIds: state.entities?.orgFilter?.orgIds,
  };
};


const mapDispatchToProps = dispatch => ({
  getEnrollmentsMetrics: pageRequest => dispatch(actions.getEnrollmentsMetrics(pageRequest)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PatientStatusInTime);
